import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import styles from "../styles.modules.scss";
import { FormattedMessage } from "react-intl";
import { Button } from "../../../button";
import { useElementOnScreen } from "../../../../hooks/use-element-on-screen";
import { useSendCommand } from "../../../../hooks/use-send-command";
import { useOpenNewsArticle } from "../../../../hooks/use-open-news-article";
import { handleCTAClick } from "..";
import { parseCTAs } from "../parse-ctas";
import { translate } from "./translations";
import { usePollContext } from "../../../../stores/poll";
import { ProgressSlideType } from "../progress";

type FallingEgg = {
    id: number;
    x: number;
    duration: number;
    scale: number;
    zIndex: number;
    rotation: number;
    imageUrl: string;
};

const MAX_EGGS = 20;

const EggRainLayer = ({
    eggs,
    onAnimationEnd,
}: {
    eggs: FallingEgg[];
    onAnimationEnd: (id: number) => void;
}) => (
    <>
        {eggs.map((egg) => (
            <img
                key={egg.id}
                className="falling-egg"
                src={egg.imageUrl}
                alt=""
                width={64}
                height={64}
                onAnimationEnd={() => onAnimationEnd(egg.id)}
                style={
                    {
                        "--egg-x": `${egg.x}%`,
                        "--egg-duration": `${egg.duration}s`,
                        "--egg-scale": egg.scale,
                        "--egg-rotation": `${egg.rotation}deg`,
                    } as React.CSSProperties
                }
            />
        ))}
    </>
);

const useEggRain = (images: string[]) => {
    const [eggs, setEggs] = useState<FallingEgg[]>([]);
    const nextId = useRef(0);
    const imagesRef = useRef(images);
    const prevKeyRef = useRef(images.join(","));

    const key = images.join(",");
    if (key !== prevKeyRef.current) {
        imagesRef.current = images;
        prevKeyRef.current = key;
    }

    useEffect(() => {
        if (imagesRef.current.length === 0) return;

        const interval = setInterval(() => {
            const imgs = imagesRef.current;
            const id = nextId.current++;
            setEggs((prev) => {
                if (prev.length >= MAX_EGGS) return prev;

                const isForeground = Math.random() > 0.6;
                const egg: FallingEgg = {
                    id,
                    x: Math.random() * 100,
                    duration: 1.5 + Math.random() * 1.5,
                    scale: isForeground
                        ? 2.0 + Math.random() * 2.5
                        : 1.6 + Math.random() * 1.2,
                    zIndex: isForeground ? 3 : 0,
                    rotation:
                        (Math.random() > 0.5 ? 1 : -1) * Math.random() * 360,
                    imageUrl: imgs[Math.floor(Math.random() * imgs.length)],
                };

                return [...prev, egg];
            });
        }, 300);

        return () => clearInterval(interval);
    }, []);

    const handleAnimationEnd = useCallback((id: number) => {
        setEggs((prev) => prev.filter((e) => e.id !== id));
    }, []);

    const backgroundEggs = useMemo(
        () => eggs.filter((e) => e.zIndex === 0),
        [eggs],
    );
    const foregroundEggs = useMemo(
        () => eggs.filter((e) => e.zIndex === 3),
        [eggs],
    );

    return { backgroundEggs, foregroundEggs, handleAnimationEnd };
};

const DigitSlot = ({
    digit,
    countdown,
}: {
    digit: string;
    countdown: boolean;
}) => {
    const prevRef = useRef(digit);
    const [roll, setRoll] = useState<{
        from: string;
        to: string;
        key: number;
    } | null>(null);

    if (digit !== prevRef.current) {
        setRoll({ from: prevRef.current, to: digit, key: Date.now() });
        prevRef.current = digit;
    }

    return (
        <div className="digit-slot">
            {roll ? (
                <div
                    className={`digit-column ${countdown ? "roll-down" : "roll-up"}`}
                    key={roll.key}
                >
                    {countdown ? (
                        <>
                            <div className="digit-cell">{roll.to}</div>
                            <div className="digit-cell">{roll.from}</div>
                        </>
                    ) : (
                        <>
                            <div className="digit-cell">{roll.from}</div>
                            <div className="digit-cell">{roll.to}</div>
                        </>
                    )}
                </div>
            ) : (
                <div className="digit-column">
                    <div className="digit-cell">{digit}</div>
                </div>
            )}
        </div>
    );
};

const TICK_MS = 43; // ~23 ticks per second
const UPDATE_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

const CountingNumber = ({
    value,
    animate,
    countdown,
    lang,
}: {
    value: number;
    animate: boolean;
    countdown: boolean;
    lang: string;
}) => {
    const [displayValue, setDisplayValue] = useState(() => {
        // Figure out how far into the current 5-min update window we are,
        // then only offset by the remaining ticks until the next update.
        const elapsed = Date.now() % UPDATE_INTERVAL_MS;
        const remaining = UPDATE_INTERVAL_MS - elapsed;
        const remainingTicks = Math.round(remaining / TICK_MS);
        // Count-up starts below target, countdown starts above target
        return countdown
            ? value + remainingTicks
            : Math.max(0, value - remainingTicks);
    });
    const timeoutRef = useRef<ReturnType<typeof setTimeout>>(null);

    useEffect(() => {
        if (!animate) return;

        const direction = countdown ? -1 : 1;

        const scheduleNext = () => {
            timeoutRef.current = setTimeout(() => {
                setDisplayValue((prev) => {
                    const next = prev + direction;
                    // Stop when we reach the target value
                    if (!countdown && next >= value) return value;
                    if (countdown && next <= value) return value;
                    return next;
                });
                // Keep ticking only if we haven't reached the target
                setDisplayValue((current) => {
                    if (current !== value) {
                        scheduleNext();
                    }
                    return current;
                });
            }, TICK_MS);
        };

        scheduleNext();

        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, [animate, countdown, value]);

    const formatted = displayValue.toLocaleString().replace(/,/g, " ");

    return (
        <div className="progress-value rolling-number">
            {formatted.split("").map((char, i) => {
                if (char === " ") {
                    return (
                        <span key={`sep-${i}`} className="digit-separator">
                            {"\u2009"}
                        </span>
                    );
                }

                return (
                    <DigitSlot
                        key={`${i}-${formatted.length}`}
                        digit={char}
                        countdown={countdown}
                    />
                );
            })}
            <span className="eggs-label">
                {translate("chocolateEggsCollected", lang)}
            </span>
        </div>
    );
};

export const GoldenEggProgressSlide = ({
    slide,
}: {
    slide: ProgressSlideType;
}) => {
    const { title, tracker, background, targeting, relatedMedia } = slide;

    const [ref, isVisible] = useElementOnScreen<HTMLDivElement>();
    const sendCommand = useSendCommand();
    const openNewsArticle = useOpenNewsArticle();
    const {
        actions: { openModal },
    } = usePollContext();

    const handleClick = handleCTAClick(
        sendCommand,
        openNewsArticle,
        openModal,
        title,
    );

    const ctas = parseCTAs(slide.ctas, handleClick);

    const isCountdown =
        relatedMedia?.find((media) => media.title === "direction")?.label ===
        "countdown";

    const spotlightImages = [1, 2, 3, 4]
        .map(
            (n) =>
                relatedMedia?.find((media) => media.title === `spotlight-${n}`)
                    ?.image,
        )
        .filter(Boolean);

    const spotlightImageUrls = spotlightImages
        .map((img) => img?.url)
        .filter(Boolean) as string[];

    const [spotlightIndex, setSpotlightIndex] = useState(0);
    const [bounceKey, setBounceKey] = useState(0);

    useEffect(() => {
        if (spotlightImages.length <= 1 || !isCountdown) return;

        const interval = setInterval(() => {
            setSpotlightIndex((prev) => (prev + 1) % spotlightImages.length);
            setBounceKey(Date.now());
        }, 2000);

        return () => clearInterval(interval);
    }, [spotlightImages.length, isCountdown]);

    const spotlightImage = spotlightImages[spotlightIndex];

    const teaserImage = isCountdown
        ? relatedMedia?.find((media) => media.title === "teaser")?.image
        : undefined;

    const dayLabel = relatedMedia?.find(
        (media) => media.title === "day",
    )?.label;

    const progressValue = tracker.progressValue ?? 0;

    const { backgroundEggs, foregroundEggs, handleAnimationEnd } = useEggRain(
        isCountdown ? [] : spotlightImageUrls,
    );

    return (
        <div
            className={`slide progress-indicator ${styles.slide} ${
                isVisible ? "visible" : ""
            } ${targeting?.campaignId ?? ""} ${
                title ? "with-title" : "without-title"
            } ${isCountdown ? "countdown" : ""}`}
            ref={ref}
        >
            <div
                className={`content ${styles.content}`}
                style={
                    background
                        ? ({
                              "--bg-image": `url(${background.large.path})`,
                          } as React.CSSProperties)
                        : undefined
                }
            >
                {isCountdown && <div className="shine-effect" />}

                {!isCountdown && (
                    <div className="egg-rain egg-rain-back">
                        <EggRainLayer
                            eggs={backgroundEggs}
                            onAnimationEnd={handleAnimationEnd}
                        />
                    </div>
                )}

                {title && (
                    <div className={`title ${styles.title}`} data-title={title}>
                        {title}
                        {dayLabel && (
                            <span className="day-label">
                                {translate("day", slide.lang)} {dayLabel}
                            </span>
                        )}
                    </div>
                )}

                <div className="spotlight-container">
                    {isCountdown && spotlightImage?.url && (
                        <img
                            key={bounceKey}
                            className="spotlight-image bounce"
                            src={spotlightImage.url}
                            alt={spotlightImage.title || ""}
                            width={220}
                            height={220}
                        />
                    )}
                    {teaserImage?.url && (
                        <img
                            className="teaser-image"
                            src={teaserImage.url}
                            alt={teaserImage.title || ""}
                            width={220}
                            height={220}
                        />
                    )}
                    <CountingNumber
                        value={progressValue}
                        animate={isVisible}
                        countdown={isCountdown}
                        lang={slide.lang}
                    />
                </div>

                {!isCountdown && (
                    <div className="egg-rain egg-rain-front">
                        <EggRainLayer
                            eggs={foregroundEggs}
                            onAnimationEnd={handleAnimationEnd}
                        />
                    </div>
                )}

                <div className={`wrapper ${styles.wrapper}`}>
                    {ctas.length ? (
                        <div
                            className={`ctas ${
                                ctas.length === 1 ? "single" : "multi"
                            } ${styles.ctas}`}
                        >
                            {ctas.map(({ label, variant, onClick }, index) => {
                                return (
                                    <Button
                                        key={index}
                                        onClick={onClick}
                                        variant={variant}
                                    >
                                        <span className="buttonTextWrapper">
                                            {label || (
                                                <FormattedMessage id="globalText.langGo" />
                                            )}
                                        </span>
                                    </Button>
                                );
                            })}
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};
